import axios from "axios";
import router from "@/router";
import store from "./store";
import { gerarHash, gerarNonce, canonicalStringify } from "@/utils/hash.js";
import { decryptDynamicResponse } from "@/utils/cryptoDyna";

const apis = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL_API, 
  headers: {
    'X-APP-VER': process.env.VUE_APP_VERSION || '0.0.1',
  },
});

apis.defaults.timeout = 35000;

// Flag para evitar múltiplas chamadas de refresh simultâneas
let isRefreshing = false;
let failedQueue: any[] = [];

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
}

// Adiciona token se estiver presente 
apis.interceptors.request.use(async (config:any) => {
  store.commit("setLoading", true);

  store.commit("setProgress", 0);

  // Simulação de progresso (vai aumentando aos poucos)
  const timer = setInterval(() => {
    const current = store.state.progress;
    // vai até 90% no máximo, deixa o final pra resposta
    if (current < 90) {
      const next = current + Math.random() * 10;
      store.commit("setProgress", Math.min(next, 90));
    }
  }, 300);

  store.commit("setProgressTimer", timer);
  
  const token = store.state.user?.access_token || localStorage.getItem('access_token');
  if (token) {
    config.headers = config.headers || {}; // <-- Garantia de que headers existe
    config.headers['Authorization'] = `Bearer ${token}`;
  }



  if ( (config.method === "post" || config.method === "put" || config.method === "patch") && config.data) {
    const meta = {
      timestamp: Date.now(),
      nonce: gerarNonce(),
    };

    // Gera o hash com base no corpo e metadados
    const hash = await gerarHash(canonicalStringify({
      data: config.data,
      meta,
    }));

    // Apenas adiciona headers, sem tocar no corpo original
    config.headers["X-Content-Hash"] = hash;
    config.headers["X-Timestamp"] = meta.timestamp;
    config.headers["X-Nonce"] = meta.nonce;


  }

  return config;
}, (error) => {

  console.log(error)
  store.commit("setLoading", false);
  return Promise.reject(error);
});




apis.interceptors.response.use(
  async (response) => {
    store.commit("setLoading", false);

    // 🔐 Verifica se a API retornou resposta criptografada
    if (response.data && response.data.enc) {
      try {
        const decrypted = decryptDynamicResponse(response.data.enc);
        response.data = decrypted; // substitui por JSON real
      } catch (e) {
        console.error("Erro ao descriptografar resposta:", e);
      }
    }

    return response;
  },
  async (error) => {
    store.commit("setLoading", false);

    const originalRequest = error.config;

    // 🔐 Aqui também precisa descriptografar respostas de erro da API
    if (error.response?.data?.enc) {
      try {
        const decrypted = decryptDynamicResponse(error.response.data.enc);
        error.response.data = decrypted;
      } catch (e) {
        console.error("Erro ao descriptografar erro:", e);
      }
    }

    // 🔄 Tratamento do 401 (refresh token)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = store.state.user?.refresh_token || localStorage.getItem('refresh_token');

      if (!refreshToken) {
        router.push("/signin");
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apis(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        // 🔐 IMPORTANTE: refresh-token também virá criptografado
        const refreshResponse = await axios.post(
          "https://api.jslogistica.fintail.com.br/api/auth/refresh-token", 
          { refresh_token: refreshToken }
        );

        let data = refreshResponse.data;

        if (data.enc) {
          data = decryptDynamicResponse(data.enc);
        }

        const newToken = data.access_token;
        const newRefresh = data.refresh_token;

        // Atualiza store e localStorage
        store.commit("setUserToken", { access_token: newToken, refresh_token: newRefresh });
        localStorage.setItem("access_token", newToken);
        localStorage.setItem("refresh_token", newRefresh);

        apis.defaults.headers.common["Authorization"] = "Bearer " + newToken;
        originalRequest.headers["Authorization"] = "Bearer " + newToken;

        processQueue(null, newToken);
        return apis(originalRequest);
      } catch (err) {
        processQueue(err, null);
        store.commit("logout");
        router.push("/signin");
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);


export default apis;
